/**
 * Identity configuration — who the site is *about*.
 *
 * @remarks
 * Search engines build a knowledge graph around entities, not pages. This file describes the one
 * entity that owns the site: a `Person` for a portfolio or personal site, an `Organization` for a
 * company or product site. Set {@link IdentityConfig.type} and the schema layer emits the matching
 * entity — nothing else in the codebase needs to change.
 *
 * **Everything below the contact block is an entity-reconciliation signal.** A knowledge panel is
 * not awarded for describing yourself well; it is awarded when a consumer can match what the site
 * claims against something it already knows. The fields are ordered by how much each contributes to
 * that:
 *
 * 1. `identifiers` — checkable against a registry (`ORCID`, a company number). The strongest signal
 *    here, and the one most sites omit.
 * 2. `profiles` — only worth something because those profiles link back. Emitted as `sameAs` in the
 *    JSON-LD *and* as `<link rel="me">` in the head, from this one array, because a claim that
 *    exists on one side only is unverifiable by design.
 * 3. `worksFor`, `affiliations`, `alumniOf`, `credentials` — ties to organisations that already
 *    exist in the graph. Anchor each on *their* domain (see {@link IdentityConfig.worksFor}).
 * 4. `topics` — a bare string is a label a consumer has to disambiguate; a `sameAs` URL is a thing
 *    it already knows.
 *
 * **Claim only what is true and public.** Everything here is published in the page source. Nothing
 * that is not already on the site belongs in it, and an over-claimed entity is worse than a sparse
 * one: a single unverifiable assertion is enough for a consumer to distrust the rest of the graph.
 * The optional fields ship empty for that reason — fill in what applies and leave the rest alone.
 *
 * **Relative imports only** — this file is reachable from `vite.config.ts`.
 * See AGENTS.md § The Thing That Breaks Silently.
 *
 * @module
 * @author Bayu Dwiyan Satria
 * @since 1.0.0
 */

/**
 * Types
 */
import type {
  CredentialInput,
  EducationInput,
  IdentifierInput,
  LanguageInput,
  OccupationInput,
  OrganizationRefInput,
  PublicationInput,
  TopicInput
} from '@bayudwiyansatria/seo'

/**
 * Shape of {@link identityConfig}.
 */
export interface IdentityConfig {
  /**
   * Which Schema.org entity anchors the graph.
   *
   * @remarks
   * `'Person'` produces a `Person` entity and, on the home route, a `ProfilePage`. `'Organization'`
   * produces an `Organization` entity and a plain `WebPage`. Google treats the two very differently
   * in knowledge panels, so pick the one that is literally true.
   */
  readonly type: 'Person' | 'Organization'
  /** Display name of the entity. */
  readonly name: string
  /**
   * Given and family name, for a `Person`.
   *
   * @remarks
   * Ignored for an `Organization`. Worth setting even though `name` covers the display case: it is
   * what lets a consumer match a citation that orders the parts differently.
   */
  readonly givenName: string
  /** Family name, for a `Person`. Ignored for an `Organization`. */
  readonly familyName: string
  /** Alternative spellings people search for. Emitted as `alternateName`. */
  readonly alternateName: readonly string[]
  /**
   * The entity's own primary website, when it is not this one. Emitted as `url`.
   *
   * @remarks
   * Empty for a site that *is* the entity's home, where the canonical origin already says it.
   */
  readonly url: string
  /**
   * The `@id` this entity is already published under, when another site publishes it.
   *
   * @remarks
   * **This is the anchoring rule from {@link IdentityConfig.worksFor}, turned on the entity itself.**
   * An `@id` is the identity of a thing, not a location: two ids are two things, however identical
   * their properties. A person whose canonical node lives at `https://their-site.example/#person`
   * and who is *also* declared at `https://this-site.example/#entity` has been split into two
   * people, and neither accumulates what the other earns.
   *
   * So when the same entity owns several sites, one of them publishes the node and the rest point at
   * that id. Set this to that id and every reference in this site's graph — the page `about`, the
   * software's `author` and `publisher` — follows it, while the node itself is still declared here
   * with the full set of properties, so the two declarations merge rather than compete.
   *
   * Leave empty on a site that is the entity's canonical home; the anchor from `site.ts` is then
   * correct and is what gets used.
   *
   * @example
   * ```ts
   * url: 'https://www.example.com',
   * canonicalId: 'https://www.example.com/#person'
   * ```
   */
  readonly canonicalId: string
  /** One-sentence summary, emitted as `description`. */
  readonly description: string
  /** Role or tagline. For a Person this is the primary `jobTitle`; for an Organization the `slogan`. */
  readonly role: string
  /**
   * Further job titles for a `Person`, in addition to {@link IdentityConfig.role}.
   *
   * @remarks
   * Ignored for an `Organization`, which has one slogan and not a list of them.
   *
   * @example
   * ```ts
   * jobTitles: ['Site Reliability Engineer']
   * ```
   */
  readonly jobTitles: readonly string[]
  /** Public contact address. Leave empty to omit the `contactPoint` entirely. */
  readonly email: string
  /**
   * Public telephone number in international format.
   *
   * @remarks
   * Leave empty unless the number is already published on the site. Structured data is a
   * publication, not a private field.
   */
  readonly telephone: string
  /** Logo or portrait, absolute URL or site-relative path. */
  readonly image: string
  /**
   * Topics the entity is known for, emitted as `knowsAbout`.
   *
   * @remarks
   * Accepts a bare string or `{ name, sameAs }`. Prefer the second: the URL reconciles the topic
   * against an entity the consumer already has, where a string leaves it to guess which `Terraform`
   * is meant.
   */
  readonly topics: readonly TopicInput[]
  /**
   * Languages the entity operates in, emitted as `knowsLanguage`.
   *
   * @remarks
   * Accepts a bare name or `{ name, code }`, where `code` is a BCP 47 tag.
   */
  readonly languages: readonly LanguageInput[]
  /**
   * Coarse location. Every field optional — omit by leaving it empty.
   *
   * @remarks
   * Emitted as `address` and, for a `Person`, as `homeLocation`. A locality and country are
   * enough; a street address on a personal site publishes more than it buys.
   */
  readonly location: {
    readonly city: string
    readonly region: string
    readonly postalCode: string
    readonly country: string
  }
  /** Where the entity works, if it differs from {@link IdentityConfig.location}. Emitted as `workLocation`. */
  readonly workLocation: string
  /** Country of citizenship, for a `Person`. Emitted as `nationality`. Empty to omit. */
  readonly nationality: string
  /**
   * The employer or parent organisation.
   *
   * @remarks
   * **Anchor it on their domain, not yours.** `id: 'https://employer.example/#organization'` can
   * merge with the entity their own site publishes; an id on this origin creates a second, weaker
   * copy of the same company that will never reconcile with the first.
   *
   * Set to `undefined` when there is none.
   *
   * @example
   * ```ts
   * worksFor: {
   *   id: 'https://employer.example/#organization',
   *   name: 'Employer Pte. Ltd.',
   *   url: 'https://employer.example'
   * }
   * ```
   */
  readonly worksFor?: OrganizationRefInput
  /**
   * Other organisations the entity belongs to, emitted as `affiliation`.
   *
   * @remarks
   * Same anchoring rule as {@link IdentityConfig.worksFor}.
   */
  readonly affiliations: readonly OrganizationRefInput[]
  /**
   * Schools and universities, emitted as `alumniOf`.
   *
   * @example
   * ```ts
   * alumniOf: [{ name: 'Example University', alternateName: 'EU', url: 'https://example.edu' }]
   * ```
   */
  readonly alumniOf: readonly EducationInput[]
  /**
   * Degrees and certifications, emitted as `hasCredential`.
   *
   * @remarks
   * `category` is free text — `'degree'` and `'certification'` are the conventional values. Give a
   * certification the `url` of its verification page: an unverifiable credential is a claim, and a
   * verifiable one is a fact.
   *
   * @example
   * ```ts
   * credentials: [
   *   {
   *     name: 'Bachelor of Science in Computer Science',
   *     category: 'degree',
   *     educationalLevel: "Bachelor's Degree",
   *     issuer: { type: 'CollegeOrUniversity', name: 'Example University', url: 'https://example.edu' }
   *   }
   * ]
   * ```
   */
  readonly credentials: readonly CredentialInput[]
  /**
   * Occupations, emitted as `hasOccupation`.
   *
   * @remarks
   * Distinct from {@link IdentityConfig.jobTitles}: a title is a string on this entity, an
   * occupation is its own node and can carry a location.
   */
  readonly occupations: readonly OccupationInput[]
  /**
   * Registry identifiers, emitted as `identifier`.
   *
   * @remarks
   * The strongest reconciliation signal available. `sameAs` asserts *this profile is also me*;
   * an identifier asserts *I am ORCID 0000-0002-…*, which is checkable against a registry that
   * neither party controls.
   *
   * @example
   * ```ts
   * identifiers: [{ propertyID: 'ORCID', value: '0000-0002-…', url: 'https://orcid.org/0000-0002-…' }]
   * ```
   */
  readonly identifiers: readonly IdentifierInput[]
  /**
   * Published works, emitted as `subjectOf`.
   *
   * @remarks
   * Each entry is a real citation with a resolvable `url`. Leave empty rather than approximate one.
   */
  readonly publications: readonly PublicationInput[]
  /**
   * Canonical profile URLs on other platforms.
   *
   * @remarks
   * Emitted as `sameAs` in JSON-LD and as `<link rel="me">` in the head, and rendered by
   * `AppFooter.vue`. Only list profiles that link back to this site — an unreciprocated claim
   * carries no weight.
   */
  readonly profiles: readonly string[]
}

/**
 * The live identity configuration.
 *
 * @remarks
 * This describes a real person, which changes what the rest of the file may contain. Everything
 * here is published in the page source of every deployment, so each value is one that is already
 * public on `bayudwiyansatria.com` — the name, the profiles, the ORCID record. Fields that would
 * assert something not visible on this site (`telephone`, `worksFor`, `credentials`,
 * `publications`) stay empty rather than being filled from elsewhere: a sparse entity reconciles,
 * an over-claimed one teaches a consumer to distrust the whole graph.
 *
 * `type` is `Person` because that is literally true, and it is not a cosmetic choice — it makes the
 * home route emit a `ProfilePage` and the graph anchor on a `Person`, which Google treats
 * differently from an `Organization` in knowledge panels.
 *
 * @example
 * ```ts
 * import { identityConfig } from '@/config/identity'
 *
 * if (identityConfig.type === 'Organization') {
 *   // ...
 * }
 * ```
 */
export const identityConfig: IdentityConfig = {
  type: 'Person',
  name: 'Bayu Dwiyan Satria',
  givenName: 'Bayu Dwiyan',
  familyName: 'Satria',
  alternateName: ['Bayu D. Satria', 'Bayu Satria'],
  /*
   * This person's canonical node is the one bayudwiyansatria.com has been publishing since launch, so
   * this site references that id rather than minting a second person on its own origin. See the field
   * documentation above — the `#person` fragment is that site's anchor override, not the default.
   */
  url: 'https://www.bayudwiyansatria.com',
  canonicalId: 'https://www.bayudwiyansatria.com/#person',
  description:
    'Software Engineer and DevOps Engineer working on Kubernetes, Terraform and multi-cloud infrastructure, and the author of this boilerplate.',
  /*
   * `role` is the primary `jobTitle` and `jobTitles` are appended to it, so the two must not
   * overlap — "Software Engineer & DevOps Engineer" here would emit the second title twice.
   */
  role: 'Software Engineer',
  jobTitles: ['DevOps Engineer'],
  email: 'bayudwiyansatria@gmail.com',
  telephone: '',
  image: '/og-image.png',
  /*
   * `knowsAbout` describes the *person*, not the site. The distinction is easy to lose in a
   * repository whose subject is a Vue template — but what this site is about is the software
   * entity's `featureList`, and a person whose stated work is Kubernetes and Terraform while their
   * topics read "Static Site Generation" contradicts their own description two fields above. The
   * `AuthorView` route renders these, so the mismatch is visible as well as machine-readable.
   *
   * Reconciled topics rather than bare strings, as the worked example of the rule in the module
   * docblock. Point each `sameAs` at the canonical page for the thing, not at a page about it.
   */
  topics: [
    { name: 'Kubernetes', sameAs: 'https://kubernetes.io' },
    { name: 'Terraform', sameAs: 'https://developer.hashicorp.com/terraform' },
    { name: 'DevOps', sameAs: 'https://en.wikipedia.org/wiki/DevOps' },
    { name: 'Cloud Computing', sameAs: 'https://en.wikipedia.org/wiki/Cloud_computing' },
    { name: 'Vue.js', sameAs: 'https://vuejs.org' },
    { name: 'TypeScript', sameAs: 'https://www.typescriptlang.org' }
  ],
  languages: [
    { name: 'English', code: 'en' },
    { name: 'Indonesian', code: 'id' }
  ],
  location: {
    city: 'Manado',
    region: '',
    postalCode: '',
    country: 'ID'
  },
  workLocation: '',
  nationality: 'Indonesia',
  /*
   * The optional entity ties stay empty: each one is a claim about an organisation that this site
   * does not otherwise mention, and an unverifiable claim is worth less than nothing here. See the
   * field docs above for the shape each expects.
   */
  worksFor: undefined,
  affiliations: [],
  alumniOf: [],
  credentials: [],
  occupations: [],
  /*
   * ORCID first: it is checkable against a registry neither this site nor its reader controls, which
   * is a stronger signal than any number of profile links.
   */
  identifiers: [
    { propertyID: 'ORCID', value: '0000-0002-5276-2619', url: 'https://orcid.org/0000-0002-5276-2619' },
    { propertyID: 'GitHub', value: 'bayudwiyansatria', url: 'https://github.com/bayudwiyansatria' }
  ],
  publications: [],
  /*
   * Only profiles that link back. The Twitter handle is `bayudsatria`, which is not the pattern the
   * other two follow — it is the account that actually exists, and a tidier-looking URL pointing at
   * nobody is exactly the unreciprocated claim `sameAs` is worthless for.
   */
  profiles: [
    'https://github.com/bayudwiyansatria',
    'https://www.linkedin.com/in/bayudwiyansatria',
    'https://twitter.com/bayudsatria'
  ]
}
